import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import { useState } from 'react'
import { getRequirementId, isRequirementRef, toRequirementRef } from '../../../../../util/applicationRequirement.util'
import { INPUT_POLICY_PATH, isApplicationInput } from '../../policy_feature/inputs'

const deriveInitialState = (policyBody) => {
  const autoModeInputs = []
  const requirementIds = {}

  Object.keys(INPUT_POLICY_PATH)
    .filter(isApplicationInput)
    .forEach((input) => {
      const values = get(policyBody, INPUT_POLICY_PATH[input]) || []
      const references = values.filter(isRequirementRef).map(getRequirementId)
      if (references.length) {
        autoModeInputs.push(input)
        requirementIds[input] = references
      }
    })

  return { autoModeInputs, requirementIds }
}

// Exposed so a caller can seed a field-visibility list with fields that already resolve via a
// stored requirement, without waiting a render for the hook itself to report it.
export const getAutoModeApplicationInputs = (policyBody) => deriveInitialState(policyBody).autoModeInputs

// Owns, for a CA template's application-bearing fields, whether each one is resolved
// automatically from one or more stored application requirements or still manually prompted at
// deploy (the pre-existing, backward-compatible default) — and keeps the field's raw JSON value
// (its "application:<name>" references) in sync with whatever is attached. An application
// requirement is just a name (like a group requirement), so there is no separate definition to
// own here — unlike a named location requirement's type/contents.
export default function useApplicationFieldRequirements({ initialPolicyBody, policyBody, setPolicyBody }) {
  const [{ autoModeInputs, requirementIds }, setState] = useState(() => deriveInitialState(initialPolicyBody))

  const isAutoMode = (input) => autoModeInputs.includes(input)
  const getAttachedRequirementIds = (input) => requirementIds[input] || []

  const writeFieldValue = (input, values) => {
    const updated = cloneDeep(policyBody) || {}
    set(updated, INPUT_POLICY_PATH[input], values)
    setPolicyBody(updated)
  }

  const toggleAutoMode = (input, enabled) => {
    setState((current) => ({
      autoModeInputs: enabled
        ? [...current.autoModeInputs, input]
        : current.autoModeInputs.filter((key) => key !== input),
      requirementIds: enabled ? current.requirementIds : { ...current.requirementIds, [input]: [] },
    }))

    if (!enabled) {
      writeFieldValue(input, [])
    }
  }

  // The single source of truth for "which requirements are on this field" — matches the
  // MultiSelectPillList/MultiSelectSearch contract of handing back the whole new selection on
  // every change, add or remove alike.
  const setAttachedRequirementIds = (input, updatedIds) => {
    setState((current) => ({ ...current, requirementIds: { ...current.requirementIds, [input]: updatedIds } }))
    writeFieldValue(input, updatedIds.map(toRequirementRef))
  }

  const hydrate = (nextPolicyBody) => {
    setState(deriveInitialState(nextPolicyBody))
  }

  return { isAutoMode, getAttachedRequirementIds, toggleAutoMode, setAttachedRequirementIds, hydrate }
}
